import {
  inject,
  provide,
  type ComputedRef,
  type InjectionKey,
  type Ref,
} from "vue";
import type { FieldObservation } from "@/types/field-observations.types";
import type { ObservationEntityRef } from "@/drawing/managers/ObservationsManager";

/**
 * Contexte de la liste d'observations du volet canvas, fourni par
 * `ObservationsMenu` à ses cartes (`ObservationCard`), où qu'elles soient
 * rendues — liste par date, sections par partie, arborescence. Une seule
 * carte est ouverte à la fois ; tout ce qui touche au plan (sélection,
 * rattachement, zoom) passe par ici plutôt que par une cascade de props et
 * d'événements à travers les listes intermédiaires.
 */
export interface ObservationsListContext {
  diagnosticId: Ref<number>;
  /** Carte ouverte (id d'observation), null si toutes sont fermées. */
  openObservationId: Ref<number | null>;
  /** Lettre de chaque observation, par id (A = la plus ancienne). */
  letters: ComputedRef<Map<number, string>>;
  /** Entités du plan rattachées, par `localId` d'observation. */
  entitiesByObservation: Ref<Map<string, ObservationEntityRef[]>>;
  /** Sélection courante sur le plan. */
  selectionHandles: Ref<string[]>;
  /** Observations dont au moins une entité est sélectionnée sur le plan. */
  selectedObservationIds: Ref<string[]>;
  /** Mode « cliquez sur les éléments à rajouter » de la carte ouverte. */
  addModeActive: Ref<boolean>;
  /**
   * Incrémenté pour ramener la carte ouverte à l'écran alors qu'elle l'est
   * déjà (badge de son cadre touché sur le plan : elle a pu être défilée hors
   * de vue, ou sa partie repliée).
   */
  revealToken: Ref<number>;
  /** Ouvre la carte (et montre ses entités sur le plan). */
  open: (observation: FieldObservation) => void;
  close: () => void;
  stampHandles: (handles: string[], observationLocalId: string) => number;
  detachHandles: (handles: string[]) => number;
  /** Détache TOUTES les entités de l'observation. */
  clearObservation: (observationLocalId: string) => number;
  /** Entre dans le mode ajout (outil sélection activé) ou en sort. */
  toggleAddMode: () => void;
  /** Ouvre la popup des marqueurs photo, pour rattacher à cette observation. */
  openMarkerPicker: (observationLocalId: string) => void;
}

const OBSERVATIONS_LIST_KEY: InjectionKey<ObservationsListContext> =
  Symbol("observationsList");

export function provideObservationsList(context: ObservationsListContext): void {
  provide(OBSERVATIONS_LIST_KEY, context);
}

export function useObservationsList(): ObservationsListContext {
  const context = inject(OBSERVATIONS_LIST_KEY);
  if (!context) {
    throw new Error("useObservationsList : no observations list provided");
  }
  return context;
}
